import React, { useContext, useEffect, useState } from "react";
import { AppBar, Toolbar, Typography, CssBaseline, Badge, Box, Container, IconButton } from "@mui/material";
import { UserDetail } from "./UserDetail";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import { MemorizedLocationSelection } from "./LocationSelection";
import PageContext from "../../components/Common/PageContext";
import { useCallback } from "react";
import ElevationScroll from "../ElevationScroll/ElevationScroll";
import { InitialAppDataContext } from "../../providers/initial-app-data";
import { UserContext } from "../../providers/user";
import { useStyles } from "./AppBar.styles";
import { isFeatureEnabledForLocation } from "../../util/featureUtil";
import { receiveStatus } from "../../util/receiveStatus";
import { JbAppBarProps } from "./types";
import { creditStatus } from "../../util/creditRequestMapping";
import { getTransferBadgeCount } from "../../util/badgeCount";

export const JbAppBar = (props: JbAppBarProps) => {
	const theme = useTheme();
	const classes = useStyles();
	const [pageContext] = useContext(PageContext);
	const {
		locations,
		featureConfiguration: { arrivals, adhocCreditRequest, transfers },
		userLocation,
		userInformation,
		badge,
	} = pageContext;

	const { countryCode } = userInformation;
	const { changeLocation } = useContext(InitialAppDataContext);
	const [badgeCount, setBadgeCount] = useState<number | string>(0);
	const { graphProfile } = useContext(UserContext);

	const isArrivalsEnabled = isFeatureEnabledForLocation(userLocation, arrivals);
	const isAdhocCreditRequestEnabled = isFeatureEnabledForLocation(userLocation, adhocCreditRequest);
	const isTransfersEnabled = isFeatureEnabledForLocation(userLocation, transfers);

	useEffect(() => {
		const receiveCount = badge.receives.reduce(
			(total: number, { status, count }: { status: string; count: number }) => {
				// Always exclude incomplete requests
				if (status === receiveStatus.pendingFinalise) {
					return total;
				}
				// Exclude arrived if arrivals feature are disabled
				if (!isArrivalsEnabled && status === receiveStatus.arrived) {
					return total;
				}
				return total + count;
			},
			0
		);
		const receiveFailuresTotal = badge.receiveFailures.length
			? badge.receiveFailures.reduce((total: number, current: { count: number; failureReason: string }) => {
					total += current.count;
					return total;
				}, 0)
			: 0;
		const creditRequestCount = badge.creditRequests.reduce(
			(total: number, { status, count }: { status: string; count: number }) => {
				// Always exclude cancelled requests
				if (status === creditStatus.cancelled) {
					return total;
				}
				// Exclude awaitingSupplier if adhoc credit requests are disabled
				if (!isAdhocCreditRequestEnabled && status === creditStatus.awaitingSupplier) {
					return total;
				}
				return total + count;
			},
			0
		);
		const transferTotal = isTransfersEnabled ? getTransferBadgeCount(badge.transfers) : 0;
		const total = receiveCount + receiveFailuresTotal + creditRequestCount + transferTotal;
		setBadgeCount(isNaN(total) ? "" : total);
	}, [badge, setBadgeCount, isArrivalsEnabled, isTransfersEnabled, isAdhocCreditRequestEnabled]);

	const onChangeLocationCallback = useCallback(
		async (value: string) => {
			await (changeLocation && changeLocation(value, countryCode));
		},
		[changeLocation, countryCode]
	);

	return (
		<React.Fragment>
			<CssBaseline />
			<ElevationScroll {...props}>
				<AppBar style={theme.palette.custom.jbAppBar} position="sticky">
					<Toolbar>
						<IconButton edge="start" color="primary" className={classes.menuButton} onClick={props.onMenuClick}>
							<Badge overlap="rectangular" badgeContent={badgeCount} color="error">
								<MenuIcon />
							</Badge>
						</IconButton>
						<Typography className={classes.title} variant="h6">
							Receiving
						</Typography>
						<MemorizedLocationSelection
							locations={graphProfile?.storeNumber === 0 ? locations.allLocations : locations.authorised.list}
							onChangeLocationCallback={onChangeLocationCallback}
						/>
						<UserDetail />
					</Toolbar>
				</AppBar>
			</ElevationScroll>
			<Container maxWidth="lg">
				<Box my={1.25} sx={{ maxWidth: "100%", overflowX: "hidden" }}>
					{props.children}
				</Box>
			</Container>
		</React.Fragment>
	);
};
